import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowRight, Truck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Seo } from "@/components/Seo";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function CartPage() {
  const { items, remove, setQty, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [addr, setAddr] = useState({ name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" });
  const [quote, setQuote] = useState(null); // {quote_id, free, rates}
  const [rateId, setRateId] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [loading, setLoading] = useState(false);

  const cartItems = items.map((i) => ({ product_id: i.product_id, color: i.color, size: i.size, quantity: i.quantity }));
  const selectedRate = quote?.rates?.find((r) => r.id === rateId);
  const shippingCost = quote ? (quote.free ? 0 : (selectedRate?.amount || 0)) : null;
  const canCheckout = quote && (quote.free || !!selectedRate);

  const addrComplete = addr.name && addr.street1 && addr.city && addr.state && addr.zip.length >= 5;

  const getRates = async () => {
    if (!addrComplete) { toast.error("Fill in your full shipping address"); return; }
    setLoadingRates(true);
    setQuote(null); setRateId(null);
    try {
      const { data } = await api.post("/shipping/rates", { address_to: addr, items: cartItems });
      setQuote(data);
      if (!data.free && data.rates?.length) setRateId(data.rates[0].id);
      if (data.free) toast.success("Free shipping unlocked 🎉");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setLoadingRates(false); }
  };

  const checkout = async () => {
    if (!email) { toast.error("Enter your email for the receipt"); return; }
    if (!canCheckout) { toast.error("Get shipping rates and pick an option first"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cartItems, origin_url: window.location.origin, email,
        quote_id: quote.quote_id, rate_id: quote.free ? null : rateId,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setLoading(false);
    }
  };

  // Re-fetch rates if cart contents change after a quote was made
  const resetQuote = () => { if (quote) { setQuote(null); setRateId(null); } };

  const inputCls = "w-full border border-border bg-black px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white";

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-12 md:px-8" data-testid="cart-page">
      <Seo title="Your Bag — The Hoodie Project®" />
      <h1 className="mb-10 font-display text-5xl font-extrabold tracking-tighter md:text-6xl">YOUR BAG</h1>

      {items.length === 0 ? (
        <div data-testid="cart-empty">
          <p className="text-muted-foreground">Your bag is empty.</p>
          <Link to="/shop" className="mt-6 inline-block bg-white px-8 py-4 text-xs font-bold tracking-[0.2em] text-black hover:bg-neutral-300">SHOP NOW</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="border border-border">
              {items.map((i) => (
                <div key={i.key} className="flex gap-4 border-b border-border p-4 last:border-b-0" data-testid={`cart-item-${i.key}`}>
                  <img src={i.image} alt={i.name} className="h-28 w-24 object-cover" />
                  <div className="flex flex-1 flex-col justify-between">
                    <div className="flex justify-between">
                      <div>
                        <p className="font-medium">{i.name}</p>
                        <p className="text-xs text-muted-foreground">{i.color} · {i.size}</p>
                      </div>
                      <p className="font-display text-lg font-bold">${(i.price * i.quantity).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-border">
                        <button onClick={() => { setQty(i.key, i.quantity - 1); resetQuote(); }} className="px-3 py-2 hover:bg-muted" data-testid={`qty-dec-${i.key}`}><Minus className="h-3 w-3" /></button>
                        <span className="w-10 text-center text-sm">{i.quantity}</span>
                        <button onClick={() => { setQty(i.key, i.quantity + 1); resetQuote(); }} className="px-3 py-2 hover:bg-muted" data-testid={`qty-inc-${i.key}`}><Plus className="h-3 w-3" /></button>
                      </div>
                      <button onClick={() => { remove(i.key); resetQuote(); }} className="text-muted-foreground hover:text-white" data-testid={`remove-${i.key}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { clear(); resetQuote(); }} className="mt-4 text-xs tracking-widest text-muted-foreground hover:text-white">CLEAR BAG</button>

            {/* Free shipping progress */}
            {subtotal < 250 && (
              <div className="mt-6 border border-border p-4" data-testid="free-ship-progress">
                <p className="text-xs tracking-widest text-muted-foreground">
                  ADD <span className="text-white">${(250 - subtotal).toFixed(2)}</span> MORE FOR FREE SHIPPING
                </p>
                <div className="mt-3 h-1 w-full bg-muted">
                  <div className="h-1 bg-white transition-all" style={{ width: `${Math.min(100, (subtotal / 250) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="border border-border p-6">
              <h2 className="font-display text-xl font-bold tracking-tight">SUMMARY</h2>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span data-testid="cart-shipping">
                    {shippingCost === null ? "Enter address" : quote.free ? "FREE" : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>Calculated at checkout</span></div>
                {shippingCost !== null && (
                  <div className="flex justify-between border-t border-border pt-3 font-display text-lg font-bold">
                    <span>Total</span><span data-testid="cart-total">${(subtotal + shippingCost).toFixed(2)}+</span>
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="mt-6 border-t border-border pt-6">
                <label className="mb-2 block text-xs tracking-[0.2em] text-muted-foreground">EMAIL FOR RECEIPT</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@email.com" data-testid="checkout-email" className={inputCls} />
              </div>

              {/* Shipping address */}
              <div className="mt-6">
                <div className="mb-3 flex items-center gap-2 text-xs tracking-[0.2em] text-muted-foreground"><Truck className="h-4 w-4" /> SHIP TO (US)</div>
                <div className="space-y-2">
                  <input value={addr.name} onChange={(e) => { setAddr({ ...addr, name: e.target.value }); resetQuote(); }} placeholder="Full name" data-testid="ship-name" className={inputCls} />
                  <input value={addr.street1} onChange={(e) => { setAddr({ ...addr, street1: e.target.value }); resetQuote(); }} placeholder="Street address" data-testid="ship-street1" className={inputCls} />
                  <input value={addr.street2} onChange={(e) => { setAddr({ ...addr, street2: e.target.value }); resetQuote(); }} placeholder="Apt, suite (optional)" data-testid="ship-street2" className={inputCls} />
                  <input value={addr.city} onChange={(e) => { setAddr({ ...addr, city: e.target.value }); resetQuote(); }} placeholder="City" data-testid="ship-city" className={inputCls} />
                  <div className="flex gap-2">
                    <select value={addr.state} onChange={(e) => { setAddr({ ...addr, state: e.target.value }); resetQuote(); }} data-testid="ship-state" className={inputCls}>
                      <option value="">State</option>
                      {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input value={addr.zip} onChange={(e) => { setAddr({ ...addr, zip: e.target.value }); resetQuote(); }} placeholder="ZIP" data-testid="ship-zip" className={inputCls} />
                  </div>
                </div>
                <button onClick={getRates} disabled={loadingRates} data-testid="get-rates-btn"
                  className="mt-3 flex w-full items-center justify-center gap-2 border border-border py-3 text-xs font-bold tracking-[0.2em] transition-colors hover:bg-white hover:text-black disabled:opacity-50">
                  {loadingRates ? "GETTING LIVE RATES…" : "GET SHIPPING RATES"}
                </button>
              </div>

              {/* Rate options */}
              {quote && quote.free && (
                <div className="mt-4 flex items-center gap-2 border border-white p-3 text-sm" data-testid="free-ship-banner">
                  <CheckCircle2 className="h-4 w-4" /> Free shipping on this order!
                </div>
              )}
              {quote && !quote.free && (
                <div className="mt-4 space-y-2" data-testid="rate-options">
                  {quote.rates.map((r) => (
                    <button key={r.id} onClick={() => setRateId(r.id)} data-testid={`rate-option-${r.id}`}
                      className={`flex w-full items-center justify-between border px-3 py-3 text-left text-sm transition-colors ${rateId === r.id ? "border-white bg-white text-black" : "border-border hover:border-white"}`}>
                      <span>{r.provider} {r.service}{r.days ? ` · ${r.days}d` : ""}</span>
                      <span className="font-bold">${r.amount.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}

              <button onClick={checkout} disabled={loading || !canCheckout} data-testid="checkout-btn"
                className="mt-6 flex w-full items-center justify-center gap-3 bg-white py-5 text-xs font-bold tracking-[0.3em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-40">
                {loading ? "REDIRECTING…" : <>CHECKOUT <ArrowRight className="h-4 w-4" /></>}
              </button>
              <p className="mt-3 text-center text-[10px] text-muted-foreground">Live carrier rates · Secure checkout by Stripe</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
